import { useState, useMemo, Fragment } from 'react';
import dayjs from 'dayjs';
import isToday from 'dayjs/plugin/isToday';

// Material UI
import { Button, ListItemText, List, ListItem, Checkbox, Alert } from '@mui/material';

// Componentes
import CustomModal from '@components/Modal/CustomModal';

// Utilidades
import { countPastDates } from '@utils/Utilities';
import { Swal, SwalComponent } from '@utils/alerts';

// Servicios
import EventsServices from '@services/EventsServices';

dayjs.extend(isToday);

const SesionesButton = (props) => {
  const {
    data = {},
    type = 'view',
    canEdit = false,
    viewAll = false,
    onSave = () => {},
    enableColors = false,
    countAttendance = false,
    TOLERANCIA_MIN = 0,
  } = props;
  const isEdit = type === 'edit';
  const TOLERANCIA_MS = Math.max(0, Number(TOLERANCIA_MIN)) * 60 * 1000;

  const sesiones = useMemo(() => data.sesiones?.split(',') || [data.FechaFin], [data]);
  const prevAttendance = useMemo(() => data.asistencia?.split(',') || [], [data]);
  const { count } = useMemo(() => countPastDates(sesiones), [sesiones]);

  // Al editar se ven cuantas asistencias tiene, si no solo las fechas que ya pasaron
  const currentCount = useMemo(
    () => (isEdit || countAttendance ? prevAttendance.length : count),
    [isEdit, prevAttendance, count, countAttendance]
  );
  const middle = useMemo(() => (sesiones.length > 0 ? sesiones.length / 2 : 1), [sesiones]);
  const text = useMemo(() => `${currentCount} / ${sesiones.length}`, [currentCount, sesiones]);

  const [isLoading, setIsLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [newAttendance, setNewAttendance] = useState(
    sesiones.map((sesion, index) => {
      const sessionDate = new Date(sesion);

      const start = new Date(sessionDate.getTime() + (index > 0 ? TOLERANCIA_MS : 0));
      const nextSession = sesiones[index + 1] ? new Date(sesiones[index + 1]) : null;
      const end = nextSession ? new Date(nextSession.getTime() + TOLERANCIA_MS) : null;

      // Buscar si hay una asistencia en el intervalo y guardar la que se usó
      const asistenciaEncontrada = prevAttendance.find((item) => {
        const prevDate = new Date(item);
        if (end) return prevDate >= start && prevDate < end;
        else return prevDate >= start;
      });

      return {
        fecha: sesion,
        asistencia: Boolean(asistenciaEncontrada),
        prevAsistencia: asistenciaEncontrada || null,
      };
    })
  );

  const handleCheck = (index) => {
    setNewAttendance((prev) =>
      prev.map((item, i) => (i === index ? { ...item, asistencia: !item.asistencia } : item))
    );
  };

  const updateAttendance = async () => {
    try {
      setIsLoading(true);

      const params = {
        idUser: data.idUser,
        idEvento: data.idEvento,
        newAttendance,
        currentDate: dayjs().format('YYYY-MM-DD HH:mm:ss'),
      };

      const { results, message } = await EventsServices.updateAttendance(params);
      if (results) {
        onSave();
        setOpenModal(false);
        Swal.fire({ title: message, icon: 'success' });
      } else throw new Error(message);
    } catch (e) {
      Swal.fire({ title: e.message, icon: 'warning' });
    } finally {
      setIsLoading(false);
    }
  };

  return isEdit && canEdit ? (
    <>
      <Button
        onClick={() => setOpenModal(true)}
        variant={enableColors ? 'contained' : 'outlined'}
        color={enableColors ? (currentCount > middle ? 'success' : 'warning') : 'primary'}
        fullWidth
      >
        {text}
      </Button>

      <CustomModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        disableClose
        title="Fechas de las sesiones"
        maxWidth="xs"
        sizeToFullScreen="xs"
        isLoading={isLoading}
        actionButtons={[{ icon: 'save', title: 'Guardar', action: updateAttendance, color: 'primary' }]}
      >
        <SesionList sesiones={newAttendance} onCheck={handleCheck} TOLERANCIA_MIN={TOLERANCIA_MIN} />
      </CustomModal>
    </>
  ) : sesiones.length < 2 && !viewAll ? (
    text
  ) : (
    <Button
      onClick={() =>
        SwalComponent({
          title: 'Fechas de las sesiones',
          styles: { maxWidth: '400px' },
          component: (
            <SesionList
              sesiones={countAttendance ? newAttendance : sesiones}
              disabled={!canEdit}
              TOLERANCIA_MIN={TOLERANCIA_MIN}
            />
          ),
        })
      }
      variant={enableColors ? 'contained' : 'outlined'}
      color={enableColors ? (currentCount > middle ? 'success' : 'warning') : 'primary'}
      fullWidth
    >
      {text}
    </Button>
  );
};

const SesionList = ({ sesiones, onCheck, disabled = false, TOLERANCIA_MIN = 0 }) => {
  const now = dayjs();
  const TOLERANCIA_MS = Math.max(0, Number(TOLERANCIA_MIN)) * 60 * 1000;

  // Función para determinar color de fondo
  const getBackgroundColor = (sesion, index, fecha) => {
    const start = fecha.add(index > 0 ? TOLERANCIA_MS : 0, 'millisecond');
    const nextSesion = sesiones[index + 1];
    const fechaNext = nextSesion ? dayjs(nextSesion.fecha || nextSesion) : null;
    const end = fechaNext ? fechaNext.add(TOLERANCIA_MS, 'millisecond') : null;

    const asistencia = sesion.asistencia;
    const esHoy = fecha.isToday();
    const yaPaso = now.isAfter(start);
    const estaEnIntervalo = yaPaso && (end ? now.isBefore(end) : true);

    if (asistencia) return '#92c5a1'; // Verde
    else if (esHoy && estaEnIntervalo) return '#f3e56d'; // Amarillo si está en su intervalo y es hoy
    else if (yaPaso) return '#ffb6b6'; // Rojo si ya pasó sin asistencia
    else return '#c6c6ff'; // Azul futura por defecto
  };

  return (
    <Fragment>
      {TOLERANCIA_MS > 0 && (
        <Alert severity="info" sx={{ mb: 1, textAlign: 'center' }}>
          Tolerancia aplicada: {TOLERANCIA_MIN} minutos
        </Alert>
      )}

      <List sx={{ textAlign: 'center' }}>
        {sesiones.map((sesion, index) => {
          const rawFecha = sesion.fecha || sesion;
          const fecha = dayjs(rawFecha);

          const start = fecha.add(index > 0 ? TOLERANCIA_MS : 0, 'millisecond');
          const isFuture = now.isBefore(start);

          const backgroundColor = getBackgroundColor(sesion, index, fecha);

          return (
            <ListItem key={index} divider sx={{ backgroundColor }}>
              {onCheck && (
                <Checkbox
                  checked={sesion.asistencia}
                  disabled={isFuture || disabled}
                  onChange={() => (isFuture || disabled ? {} : onCheck(index))}
                />
              )}
              <ListItemText primary={fecha.format('DD MMM YYYY HH:mm:ss')} sx={{ textAlign: 'center' }} />
            </ListItem>
          );
        })}
      </List>
    </Fragment>
  );
};

export default SesionesButton;
